import React, {useEffect, useState} from "react";
import {Location, Points, Session, User} from "../types/Types";
import {Form, Card, Col, Row} from "react-bootstrap";
import LeaderboardNav from "./LeaderboardNav";
import {addNumberSuffix} from "../util/Util";
import {collection, getDocs, query, where, orderBy} from "@firebase/firestore";
import {db} from "../../firebase/FirebaseProvider";
import UserRow from "./UserRow";
import {Link} from "react-router-dom";
import PageTracking from "../auth/PageTracking";
import {
    calculatePointsAccruals, calculateTotalUserPoints,
    filterSessionsPerDayPerLocation,
    getSortedUsers,
    SessionRank
} from "../util/points/pointsUtils";
import {V2GlassPanel} from "../shared/v2/V2Glass";
import V2YearPicker from "../shared/v2/V2YearPicker";
import LeaderboardV2Rows from "./LeaderboardV2Rows";

interface Props {
    users: Array<User>;
    userMap: Map<string, User>;
    currentUser: User | undefined;
    sessionsMap: Map<string, Session>;
    locations: Array<Location>;
}

function LeaderboardYear(props: Props) {
    const getCurrentYear = (): string => {
        return new Date().getFullYear() + '';
    };

    const getYearsList = (): number[] => {
        const currentYear = new Date().getFullYear();
        const startYear = 2023;
        const years = [];
        for (let year = currentYear; year >= startYear; year--) {
            years.push(year);
        }
        years.push(1);
        return years;
    };

    const [loading, setLoading] = useState<boolean>(true);
    const [isCalculating, setIsCalculating] = useState<boolean>(true);
    const [year, setYear] = useState<string>(getCurrentYear());
    const [rankedUsers, setRankedUsers] = useState<Array<SessionRank>>();
    const [rank, setRank] = useState<number>(-1);

    const updateYear = (nextYear: string) => {
        setLoading(true);
        setIsCalculating(true);
        setRankedUsers(undefined);
        setRank(-1);
        setYear(nextYear);
    };

    const updateLegacyYear = (event: React.ChangeEvent<HTMLSelectElement & HTMLInputElement>) => {
        updateYear(event.target.value);
    };

    const getYearStartAndEnd = (year: string) => {
        if(year === '1') {
            return { startOfYear: new Date("2000-01-01T00:00:00.000Z"), endOfYear: new Date("9999-01-01T00:00:00.000Z") };
        } else {
            const startOfYear = new Date(`${year}-01-01T00:00:00.000Z`);
            const endOfYear = new Date(`${year}-12-31T23:59:59.999Z`);
            return { startOfYear, endOfYear };
        }
    };

    useEffect(() => {
        (async () => {
            setIsCalculating(true);
            setRankedUsers(undefined);
            
            const { startOfYear, endOfYear } = getYearStartAndEnd(year);

            // Get the BONUS POINTS
            let bonusPoints = new Array<Points>();
            const bonusPointsOnceCollection = query(collection(db, 'points'),
                where('recordedAt', '>', startOfYear),
                where('recordedAt', '<', endOfYear),
                where('earnedBy', '==', 'BONUS_CHECK_IN')
            );
            const bonusPointsCollectionSnapshot = await getDocs(bonusPointsOnceCollection);
            bonusPointsCollectionSnapshot.forEach((doc) => {
                let bonusPointsToAdd = doc.data() as Points;
                bonusPoints.push(bonusPointsToAdd)
            });

            // Get the SESSIONS
            let sessions: Array<Session> = [];
            const sessionOnceCollection = query(collection(db, 'sessions'),
                where('checkInTime', '>', startOfYear),
                where('checkInTime', '<', endOfYear),
                orderBy('checkInTime', 'asc'));
            const sessionOnceCollectionSnapshot = await getDocs(sessionOnceCollection);
            sessionOnceCollectionSnapshot.forEach((doc) => {
                let session = doc.data() as Session;
                sessions.push(session);
            });
            
            // PROCESS THE DATA SYNCHRONOUSLY - all at once
            const filteredSessions = filterSessionsPerDayPerLocation(sessions);
            const accruals = calculatePointsAccruals(filteredSessions, bonusPoints, props.locations);
            const userPoints = calculateTotalUserPoints(accruals);
            const usersById = new Map(props.userMap);
            if (props.currentUser && !props.currentUser.socialDisabled) {
                // The cached user map can be missing the live user (for example when
                // their status is not yet reflected in the initial active-user query).
                usersById.set(props.currentUser.id, props.currentUser);
            }
            const usersToRank = new Array<SessionRank>();
            userPoints.forEach((userPoints) => {
                if(usersById.get(userPoints.userId) !== undefined) {
                    usersToRank.push({
                        user: usersById.get(userPoints.userId),
                        points: userPoints.points
                    } as SessionRank)
                }
            });
            const sortedUsers = getSortedUsers(usersToRank);
            
            // Calculate rank
            let calculatedRank = 0;
            sortedUsers.forEach((rankedUser, index) => {
                if(props.currentUser && rankedUser.user && rankedUser.user.id === props.currentUser.id) {
                    calculatedRank = index + 1;
                }
            });
            
            // Update all state at once after processing is complete
            setRankedUsers(sortedUsers);
            setRank(calculatedRank);
            setLoading(false);
            setIsCalculating(false);
        })();
    }, [year, props.userMap, props.currentUser, props.locations]);

    return (
        <>
            <PageTracking />
            <LeaderboardNav currentUser={props.currentUser} />
            {props.currentUser?.enableUiV2 ? <V2GlassPanel className="leaderboard-v2-list-card v2-glass-list-panel">
                <div className="leaderboard-v2-year-header">
                    <div className="leaderboard-v2-summary">
                        {isCalculating && <span>Calculating...</span>}
                        {!isCalculating && rankedUsers && rank > 0 && <span>You&apos;re ranked <b>{rank}{addNumberSuffix(rank)}</b> out of <b>{rankedUsers.length}</b></span>}
                        {!isCalculating && rankedUsers && rank === 0 && <span>No points recorded yet.</span>}
                        <small>
                            <Link to="/support#points-help">How are points calculated?</Link>
                        </small>
                    </div>
                    <V2YearPicker
                        value={year}
                        onChange={updateYear}
                        availableYears={getYearsList().filter((availableYear) => availableYear !== 1)}
                        ariaLabel="Select points year"
                    />
                </div>
                {!isCalculating && rankedUsers && (
                    <LeaderboardV2Rows
                        rows={rankedUsers.map((sessionRank) => ({
                            user: sessionRank.user,
                            value: sessionRank.points,
                            session: props.sessionsMap.get(sessionRank.user.id),
                        }))}
                        linksToTab={`points?year=${year}`}
                        emptyMessage="No points recorded yet."
                    />
                )}
            </V2GlassPanel> : <Card className={"mb-3"}>
                <Card.Header>
                    <Row style={{marginLeft: "-22px", marginRight: "-22px"}}>
                        <Col className={"col-auto"}>
                            {isCalculating && <div>Calculating...</div>}
                            {!isCalculating && rankedUsers && (rank > 0) && <div>You're ranked: {rank}<small>{addNumberSuffix(rank)}</small> out of {rankedUsers.length}</div>}
                            <div>
                                <small>
                                    <Link to={"/support#points-help"}>How are points calculated?</Link>
                                </small>
                            </div>
                        </Col>
                        <Col className="col-auto ml-auto">
                            <div>
                                <Form.Control as="select" id="dropdown" value={year} onChange={updateLegacyYear}>
                                    {getYearsList().map((year) => (
                                        <option key={year} value={year}>{year === 1 ? 'Total' : year}</option>
                                    ))}
                                </Form.Control>
                            </div>
                        </Col>
                    </Row>
                </Card.Header>
                <Card.Body style={{paddingTop: "0.4em"}}>
                    {isCalculating && <div className={'loading'}>
                        Calculating...
                    </div>}
                    {!isCalculating && rankedUsers && rankedUsers.map((sessionRank: SessionRank, index: number) => (
                        <UserRow linksToTab={`points?year=${year}`} countToDisplay={sessionRank.points.toString()} key={sessionRank.user.id} index={index} user={sessionRank.user} session={props.sessionsMap.get(sessionRank.user.id)}/>
                    ))}
                </Card.Body>
            </Card>}
        </>
    );
}

export default LeaderboardYear;
